<template>
    <section class="argus-glass-surface p-3 mb-3" tabindex="-1" aria-label="Конфликты годового плана">
        <h6>Годовой план</h6>
        <p class="small mb-2">Спорных периодов: {{ conflicts.length }}. После группировки соседних одинаковых вариантов — {{ groups.length }}.
            Непересекающиеся части всех планов сохранятся. Все даты ниже — включительно.</p>
        <div class="merge-plan-bulk">
            <label class="mb-0">Взять план одной карточки для спорных периодов
                <b-form-select v-model="employeeId" :options="employeeOptions" :disabled="busy" size="sm" />
            </label>
            <b-button variant="outline-primary" size="sm" :disabled="busy || !bulkChoices.length" @click="applyEmployee">Применить выбор · {{ bulkChoices.length }}</b-button>
        </div>
        <p class="small text-muted mt-2 mb-1">Выбор заменит решения в черновике для доступных периодов. Затем нажмите «Проверить ещё раз».</p>
        <p v-if="employeeId && bulkChoices.length < conflicts.length" class="small text-warning" role="status">
            Требуют отдельного выбора: {{ conflicts.length - bulkChoices.length }}. У этой карточки нет единственного доступного варианта для этих периодов.
        </p>
        <p v-if="notice" class="small text-success" role="status">{{ notice }}</p>
        <details ref="periodDetails" :open="expanded" @toggle="expanded = $event.target.open" class="mt-2">
            <summary>Проверить и изменить отдельные периоды · нерешённых по последней проверке: {{ unresolved }}</summary>
            <div class="merge-plan-rows mt-2">
                <article v-for="group in groups" :key="group.id" class="merge-plan-row" :ref="isResolved(group) ? undefined : 'unresolved'" tabindex="-1">
                    <div><strong>{{ range(group.starts_on, group.ends_on) }}</strong>
                        <small class="d-block" :class="isResolved(group) ? 'text-success' : 'text-warning'">{{ status(group) }}</small>
                    </div>
                    <div>
                        <b-form-select :value="choice(group)" :disabled="busy || !group.allowed" size="sm"
                            :aria-label="'План на период ' + range(group.starts_on, group.ends_on)" @change="choose(group, $event)">
                            <option :value="null">{{ hasMixedChoice(group) ? 'Есть отдельные решения — раскройте детали' : 'Выберите сохраняемый вариант…' }}</option>
                            <option v-for="option in group.options" :key="option.key" :value="option.key">{{ option.row.label }}</option>
                        </b-form-select>
                        <details v-if="group.items.length > 1 || hasNotes(group)" class="small mt-1">
                            <summary>Исходные периоды и примечания ({{ group.items.length }})</summary>
                            <div v-for="item in group.items" :key="item.conflict.id" class="mt-2">
                                <label>{{ range(item.conflict.details.starts_on, item.conflict.details.ends_on) }}
                                    <b-form-select :value="draftChoice(item.conflict)" :disabled="busy || !item.conflict.allowed" size="sm"
                                        :options="[{value: null, text: 'Выберите вариант…'}].concat(item.options.map(option => ({value: String(option.row.id), text: option.row.label})))"
                                        @change="chooseOne(item.conflict, $event)" />
                                </label>
                                <p v-for="option in item.options.filter(option => option.row.note || option.row.loupe_reason)" :key="option.key" class="mb-1">{{ option.row.label }} · {{ option.row.loupe_reason }}</p>
                            </div>
                        </details>
                    </div>
                </article>
            </div>
        </details>
    </section>
</template>
<script>
import {planGroups, groupChoice, employeePlanChoices, planRange} from './employeeMergePlanGroups';
export default {
    name: 'EmployeeMergePlanChoices',
    props: {conflicts: {type: Array, required: true}, employees: {type: Array, required: true}, resolutions: {type: Object, required: true},
        accepted: {type: Object, default: () => ({})}, busy: Boolean},
    data: () => ({employeeId: null, expanded: false, notice: ''}),
    computed: {
        groups() {return planGroups(this.conflicts);},
        unresolved() {return this.conflicts.filter(row => !row.resolved).length;},
        employeeOptions() {return [{value: null, text: 'Выберите карточку…'}].concat(this.employees.map(person => ({value: Number(person.employee_id),
            text: [person.last_name, person.first_name, person.middle_name].filter(Boolean).join(' ') + ` · #${person.employee_id}`})));},
        bulkChoices() {return this.employeeId ? employeePlanChoices(this.conflicts, this.employeeId) : [];}
    },
    methods: {
        range: planRange,
        draftChoice(conflict) {const draft = this.resolutions[conflict.id]; return draft && draft.fingerprint === conflict.fingerprint ? draft.choice : null;},
        choice(group) {return groupChoice(group, this.resolutions);},
        hasMixedChoice(group) {return !this.choice(group) && group.items.some(item => (this.resolutions[item.conflict.id] || {}).choice);},
        hasNotes(group) {return group.options.some(option => option.row.note || option.row.loupe_reason);},
        isResolved(group) {return group.items.every(({conflict}) => {
            const accepted = this.accepted[conflict.id], draft = this.resolutions[conflict.id];
            return conflict.resolved && accepted && draft && accepted.fingerprint === draft.fingerprint && String(accepted.choice) === String(draft.choice);
        });},
        status(group) {
            if (!group.allowed) return group.items.some(item => item.conflict.status === 'forbidden') ? 'Недостаточно прав' : 'Заблокировано';
            if (group.items.some(item => item.conflict.decision_stale && !this.draftChoice(item.conflict))) return 'Данные изменились — выберите заново';
            return this.isResolved(group) ? 'Решено' : this.choice(group) ? 'Выбрано — нужна проверка' : 'Нужно решение';
        },
        chooseOne(conflict, id) {
            if (this.busy || !conflict.allowed || id == null) return;
            if (!(conflict.candidates || []).some(row => String(row.id) === String(id))) return;
            this.$emit('decision', {id: conflict.id, value: {fingerprint: conflict.fingerprint, choice: String(id), reason: (this.resolutions[conflict.id] || {}).reason || ''}});
        },
        choose(group, key) {
            for (const item of group.items) {const option = item.options.find(option => option.key === key); if (option) this.chooseOne(item.conflict, option.row.id);}
        },
        applyEmployee() {
            if (this.busy) return;
            this.bulkChoices.forEach(({conflict, row}) => this.chooseOne(conflict, row.id));
            this.notice = `Выбор внесён в черновик. Периодов: ${this.bulkChoices.length}. Проверьте результат перед объединением.`;
        },
        async focusUnresolved() {
            this.expanded = true; await this.$nextTick();
            const target = (this.$refs.unresolved || [])[0] || this.$el; target.focus({preventScroll: true}); target.scrollIntoView({block: 'nearest'});
        }
    }
};
</script>
<style scoped>
.merge-plan-bulk {display: flex; align-items: flex-end; flex-wrap: wrap; gap: 8px;}
.merge-plan-bulk label {flex: 1 1 260px; font-size: .875rem;}
.merge-plan-row {display: grid; grid-template-columns: 215px minmax(0, 1fr); gap: 8px; padding: 10px 0; border-bottom: 1px solid #dde5eb;}
.merge-plan-row:focus {outline: 2px solid #6697bd; outline-offset: 2px;}
.merge-plan-row label {display: block;}
@media(max-width: 767px) {.merge-plan-row {grid-template-columns: minmax(0, 1fr);}}
</style>
